'use client';

import { FC } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { useLongTermQuote } from './LongTermQuoteContext';
import styles from './LongTermLeasePromoBanner.module.scss';
const BANNER_IMG = (n: number) => `https://hotdesk-assets.s3.eu-west-1.amazonaws.com/landing_pages/web_v2/banner/Workspace-${n}.webp`;

// One marquee row, so the banner matches ExploreMembershipOfferBanner's size
// (the explore page shows one or the other in the same slot).
const ROW_IMAGES = [1, 2, 3, 4, 5, 6].map(BANNER_IMG);
export interface LongTermLeasePromoBannerProps {
  onDismiss?: () => void;
}

/** Banner-shaped placeholder while long-term search settles. */
export const LongTermLeasePromoBannerSkeleton: FC = () => <section className={`${styles.banner} ${styles.bannerSkeleton}`} aria-hidden="true" data-sentry-component="LongTermLeasePromoBannerSkeleton" data-sentry-source-file="LongTermLeasePromoBanner.tsx">
        <div className={styles.content}>
            <span className={`${styles.shimmer} ${styles.skelBadge}`} />
            <span className={`${styles.shimmer} ${styles.skelTitle}`} />
            <span className={`${styles.shimmer} ${styles.skelDesc}`} />
            <span className={`${styles.shimmer} ${styles.skelDescShort}`} />
            <div className={styles.skelActions}>
                <span className={`${styles.shimmer} ${styles.skelCta}`} />
                <span className={`${styles.shimmer} ${styles.skelCtaSecondary}`} />
            </div>
        </div>
        <div className={styles.gallery}>
            <div className={styles.skelGalleryRow}>
                {[0, 1, 2].map(i => <span key={i} className={`${styles.shimmer} ${styles.skelThumb}`} />)}
            </div>
        </div>
    </section>;
const MarqueeRow: FC<{
  images: string[];
}> = ({
  images
}) => {
  // Duplicate the set so the CSS loop can scroll -50% seamlessly.
  const track = [...images, ...images];
  return <div className={styles.marqueeViewport} aria-hidden data-sentry-component="MarqueeRow" data-sentry-source-file="LongTermLeasePromoBanner.tsx">
            <div className={`${styles.marqueeTrack} ${styles.marqueeLeft}`}>
                {track.map((src, index) => <div key={`${src}-${index}`} className={styles.thumb}>
                        <Image src={src} alt="" fill className={styles.thumbImg} sizes="240px" unoptimized />
                    </div>)}
            </div>
        </div>;
};
const LongTermLeasePromoBanner: FC<LongTermLeasePromoBannerProps> = ({
  onDismiss
}) => {
  const t = useTranslations('longTermLeasePromo');
  const router = useRouter();
  const longTermQuote = useLongTermQuote();
  const geoLocation = useAppSelector((s: RootState) => s.geoLocation.geoLocationZipData);
  const handleClose = () => {
    onDismiss?.();
  };
  const handleGetStarted = () => {
    firebaseAnalytics('long_term_lease_promo_get_started_clicked');
    longTermQuote?.requestQuote({
      spaceName: '',
      listingName: '',
      city: geoLocation?.city || geoLocation?.formatted_address?.split(',')[0]?.trim() || '',
      listingId: null,
      workspaceId: null,
      listingTypeId: null,
      source: 'banner'
    });
  };
  const handleSavingsCalculator = () => {
    firebaseAnalytics('calculator_clicked', {
      source: 'long_term_promo'
    });
    router.push('/calculator');
  };
  return <section className={styles.banner} aria-labelledby="long-term-lease-promo-title" role="button" tabIndex={0} onClick={handleGetStarted} onKeyDown={e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleGetStarted();
    }
  }} data-sentry-component="LongTermLeasePromoBanner" data-sentry-source-file="LongTermLeasePromoBanner.tsx">
            <button type="button" className={styles.closeBtn} onClick={e => {
      e.stopPropagation();
      handleClose();
    }} aria-label={t('close')}>
                <X size={14} strokeWidth={2.5} color="#fff" data-sentry-element="X" data-sentry-source-file="LongTermLeasePromoBanner.tsx" />
            </button>

            <div className={styles.content}>
                <span className={styles.badge}>{t('badge')}</span>
                <h2 id="long-term-lease-promo-title" className={styles.title}>
                    {t('title')}
                </h2>
                <p className={styles.description}>{t('description')}</p>
                <div className={styles.actions}>
                    <button type="button" className={styles.ctaPrimary} onClick={e => {
          e.stopPropagation();
          handleGetStarted();
        }}>
                        {t('cta')}
                    </button>
                    <button type="button" className={styles.ctaSecondary} onClick={e => {
          e.stopPropagation();
          handleSavingsCalculator();
        }}>
                        {t('savingsCalculator')}
                    </button>
                </div>
            </div>

            <div className={styles.gallery} aria-hidden dir="ltr">
                <MarqueeRow images={ROW_IMAGES} data-sentry-element="MarqueeRow" data-sentry-source-file="LongTermLeasePromoBanner.tsx" />
            </div>
        </section>;
};
export default LongTermLeasePromoBanner;